/**
 * Weitzman cart + checkout
 *
 * Everything between "tap Add to Cart" and "land on Shopify checkout" lives
 * here, in funnel order:
 *
 *   1. CART STATE      what the bag badge says (gift wrap excluded)
 *   2. CART SURFACES   the two places a cart is drawn: the /cart page section
 *                      and the header drawer. refreshCartUI() picks the one
 *                      on screen; mutations must call that, never a surface
 *                      directly.
 *   3. ADD TO CART     WTZ.addToCart() is the one POST to /cart/add.js. The
 *                      three buttons that add (quick-add, quickshop modal,
 *                      PDP form) call it, then the cart:updated listener
 *                      updates the badge and opens the drawer.
 *   4. LINE EDITS      remove, +/-, typed quantity, gift wrap toggle, and
 *                      the reconcile that keeps each wrap's quantity equal
 *                      to the item it wraps
 *   5. DISCOUNT CODE   hands off to Shopify's /discount/{code} route
 *   6. CHECKOUT GATE   reconciles gift wrap, then always reaches
 *                      /checkout (fail-open, 1.5s ceiling)
 *   7. EXPERIMENT TAG  labels the cart with the theme name for the A/B test
 *
 * Callers outside this file: weitzman-quick-add.js, weitzman-quickshop-modal.js
 * and weitzman-product.js (all via WTZ.addToCart). Markup it drives:
 * sections/weitzman-cart.liquid, snippets/weitzman-cart-drawer.liquid,
 * templates/cart.drawer.liquid. Drawer CSS is in weitzman-2026.css because
 * {% stylesheet %} is silently dropped from snippets.
 *
 * Loads deferred straight after weitzman-2026.js, which provides
 * WTZ.fetchJSON, WTZ.toast and WTZ.trapFocus.
 */
(function () {
  'use strict';

  const routes = window.Theme && window.Theme.routes ? window.Theme.routes : {};
  const fetchJSON = window.WTZ.fetchJSON;
  const toast = window.WTZ.toast;
  const trapFocus = window.WTZ.trapFocus;
  function $$(sel, ctx) { return Array.from((ctx || document).querySelectorAll(sel)); }

  /* ==================================================================
     1. CART STATE
     ================================================================== */
  /* Gift wrap is added to the cart as its own line, linked to the item it
     wraps by a `_gift_wrap_for` line item property. Shopify's item_count
     knows nothing about that relationship, so wrapping one book reports two
     items and the bag badge reads wrong for the rest of the session.

     The wrap is packaging for something already counted, not a second thing
     the shopper bought. It stays on the order as its own line, which is
     correct — the store charges for it and has to pick it.

     Detected by the property rather than by product id, because the id
     lives in theme settings that this script cannot read. Any line carrying
     `_gift_wrap_for` is by definition a wrap for another line.

     The Liquid counterpart is snippets/wz-cart-item-count.liquid; the two
     must agree or the badge will flicker between a server-rendered number
     and a JS one. */
  function shopperItemCount(cart) {
    if (!cart || typeof cart.item_count !== 'number') return 0;
    if (!Array.isArray(cart.items)) return cart.item_count;
    return cart.items.reduce(function (total, line) {
      const props = line.properties || {};
      if (props._gift_wrap_for) return total;
      return total + (line.quantity || 0);
    }, 0);
  }

  function updateCartCount(count) {
    $$('.wtz-bagcount, [data-cart-count]').forEach(function (el) {
      el.textContent = count;
      /* The label is interpolated once by Liquid and would otherwise keep
         announcing the count the page was rendered with. Screen readers
         prefer an accessible name over the text node, so a stale label
         means the bag is misreported for the rest of the session. */
      el.setAttribute('aria-label', count === 1 ? '1 item in bag' : count + ' items in bag');
      el.classList.toggle('is-empty', count === 0);
      if (count === 0) el.setAttribute('hidden', '');
      else el.removeAttribute('hidden');
    });
    /* The drawer title's "· 2 items" sits outside the body that
       refreshCartDrawer swaps, so it is updated here or it keeps the count
       the page loaded with. */
    $$('[data-cart-drawer-count]').forEach(function (el) {
      el.textContent = '· ' + count + (count === 1 ? ' item' : ' items');
      if (count === 0) el.setAttribute('hidden', '');
      else el.removeAttribute('hidden');
    });
  }

  function refreshCartState() {
    return fetchJSON((routes.cart_url || '/cart') + '.js').then(function (cart) {
      updateCartCount(shopperItemCount(cart));
      return cart;
    }).catch(function (err) { console.error('Cart state refresh failed:', err); });
  }

  /* ==================================================================
     2. CART SURFACES
     ================================================================== */
  /* Refresh whichever cart surface the shopper is actually looking at.
     refreshCartSection only exists on /cart (it needs .wtz-cart-wrapper) and
     returns a resolved promise everywhere else, so a line mutated from the
     DRAWER used to change the cart server-side while the drawer kept showing
     the old row: remove looked broken and the item appeared to come back.
     Every cart mutation must go through this, never refreshCartSection
     directly, or it silently no-ops off the cart page. */
  function refreshCartUI() {
    if (document.querySelector('.wtz-cart-wrapper')) return refreshCartSection();
    if (document.querySelector('[data-cart-drawer]')) return refreshCartDrawer();
    return refreshCartState();
  }

  /* Section Rendering: fetch the server-rendered cart section HTML and swap
     it in place, preserving scroll position. Used by gift wrap and anything
     else that changes line items on the cart page without a full reload. */
  function refreshCartSection() {
    var wrapper = document.querySelector('.wtz-cart-wrapper');
    if (!wrapper) return Promise.resolve();
    var sectionEl = wrapper.closest('[id^="shopify-section-"]');
    var sectionId = sectionEl ? sectionEl.id.replace('shopify-section-', '') : 'main';
    var scrollY = window.scrollY;
    /* The mobile cart keeps its line items in a <details>, whose open state
       lives in the DOM and so does not survive the innerHTML swap below.
       Without this a shopper changing a quantity would have the list snap
       shut under them on every edit. Restored with scroll position, for the
       same reason: both are view state the re-render is not meant to reset. */
    const accOpen = !!document.querySelector('[data-cart-acc][open]');
    /* section_id, not sections. On /cart the ?sections= form is swallowed by
       the Cart AJAX API, which ignores the parameter and answers with cart
       JSON, so the section HTML never arrived and every edit left the line
       untouched until a full reload. section_id returns the markup directly.

       What comes back is the whole <section id="shopify-section-..."> wrapper,
       so its inner HTML is extracted rather than assigned straight into the
       existing wrapper, which would nest a section inside itself. This is what
       Dawn's getSectionInnerHTML does. */
    return fetch(window.location.pathname + '?section_id=' + encodeURIComponent(sectionId))
      .then(function (r) { return r.text(); })
      .then(function (html) {
        if (!html || !sectionEl) return;
        var fetched = new DOMParser().parseFromString(html, 'text/html')
          .querySelector('[id^="shopify-section-"]');
        sectionEl.innerHTML = fetched ? fetched.innerHTML : html;
        var acc = sectionEl.querySelector('[data-cart-acc]');
        if (acc) acc.open = accOpen;
        window.scrollTo(0, scrollY);
        refreshCartState();
      })
      /* Without this the cart silently keeps showing the quantities it had
         before the edit, and the callers that re-enable their buttons make
         that look like a completed action. Telling the shopper to reload is
         better than letting them carry a wrong cart to checkout. */
      .catch(function (err) {
        console.error('Cart section refresh failed:', err);
        toast('We could not refresh your cart. Please reload the page.', { type: 'error' });
      });
  }

  /* ------------------------------------------------------------------
     Mini-cart drawer. Mounted once from weitzman-header.liquid, so these
     hooks exist on every page.
     ------------------------------------------------------------------ */
  function drawerEl() { return document.querySelector('[data-cart-drawer]'); }

  var cartDrawerLastFocus = null;

  function openCartDrawer() {
    var d = drawerEl();
    if (!d) return false;
    /* Remember what opened the drawer so focus can go back there on close,
       instead of being dumped at the top of the document. */
    cartDrawerLastFocus = document.activeElement;
    d.classList.add('is-open');
    d.setAttribute('aria-hidden', 'false');
    document.body.classList.add('cart-drawer-open');
    var close = d.querySelector('[data-cart-drawer-close]');
    if (close) close.focus();
    return true;
  }

  function closeCartDrawer() {
    var d = drawerEl();
    if (!d) return;
    d.classList.remove('is-open');
    d.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('cart-drawer-open');
    if (cartDrawerLastFocus) { cartDrawerLastFocus.focus(); cartDrawerLastFocus = null; }
  }

  /* Pull the server-rendered drawer body so line items, the shipping
     threshold bar, the upsell slot and the subtotal all come from Liquid
     rather than being rebuilt in JS. Mirrors refreshCartSection's approach.

     Also reads the bag count out of the SAME response via data-item-count
     (set by weitzman-cart-drawer.liquid), so confirming an add costs one
     request rather than this plus a /cart.js call just for the number. */
  function refreshCartDrawer() {
    var d = drawerEl();
    if (!d) return refreshCartState();
    return fetch((routes.cart_url || '/cart') + '?view=drawer', {
      headers: { Accept: 'text/html' }
    })
      .then(function (res) { return res.text(); })
      .then(function (html) {
        var tmp = document.createElement('div');
        tmp.innerHTML = html;
        var fresh = tmp.querySelector('[data-cart-drawer-body]');
        var current = d.querySelector('[data-cart-drawer-body]');
        if (fresh && current) current.innerHTML = fresh.innerHTML;
        if (fresh && fresh.dataset.itemCount !== undefined) {
          updateCartCount(parseInt(fresh.dataset.itemCount, 10) || 0);
        }
      })
      .catch(function (err) { console.error('Cart drawer refresh failed:', err); });
  }

  document.addEventListener('click', function (e) {
    if (e.target.closest('[data-cart-drawer-close]') || e.target.closest('[data-cart-drawer-overlay]')) {
      closeCartDrawer();
      return;
    }
    /* The header bag opens the drawer instead of navigating, but only when
       the drawer is actually present and we are not already on /cart.
       Modified clicks (new tab) and the no-drawer case keep the plain link,
       which is why this stays an <a href="/cart"> and not a button. */
    var bag = e.target.closest('[data-cart-drawer-toggle]');
    if (!bag) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    if (!drawerEl() || document.querySelector('.wtz-cart-wrapper')) return;
    e.preventDefault();
    openCartDrawer();
    syncGiftWrap().then(refreshCartDrawer);
  });

  document.addEventListener('keydown', function (e) {
    var d = drawerEl();
    if (!d || !d.classList.contains('is-open')) return;
    if (e.key === 'Escape') { closeCartDrawer(); return; }
    if (e.key === 'Tab') trapFocus(d, e);
  });

  /* ==================================================================
     3. ADD TO CART
     ================================================================== */

  /* The one place the theme POSTs to /cart/add.js. Takes a FormData (the PDP
     form, so line item properties ride along) or a plain { id, quantity }.

     Resolves with the created line item after announcing it: cart:updated
     for the badge and drawer, and a toast naming the product with a View
     Cart link. Rejects with err.description set when Shopify refused the
     add (sold out, over stock), or a plain error on a network failure, so
     each caller can choose its own wording and the PDP can fall back to a
     native form POST. */
  function addToCart(body) {
    const isForm = body instanceof FormData;
    return fetch((routes.cart_add_url || '/cart/add') + '.js', {
      method: 'POST',
      headers: isForm ? { Accept: 'application/json' } : { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: isForm ? body : JSON.stringify(body)
    })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (data.status) {
          const refused = new Error(data.description || data.message || 'Could not add to cart');
          refused.description = data.description || data.message || '';
          throw refused;
        }
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: data, bubbles: true }));
        const img = data.featured_image ? data.featured_image.url || data.image : '';
        const title = data.product_title || data.title || '';
        toast('Added', {
          duration: 4000,
          html: '<span class="wtz-toast__rich">'
            + (img ? '<img src="' + img.replace(/&/g, '&amp;').replace(/"/g, '&quot;') + '" width="48" height="48" alt="">' : '')
            + '<span><strong>' + title.replace(/</g, '&lt;') + '</strong> added to cart</span>'
            + '<a href="' + (routes.cart_url || '/cart') + '" class="wtz-toast__cart-link">View Cart</a>'
            + '</span>'
        });
        return data;
      });
  }
  window.WTZ.addToCart = addToCart;

  /* Every add-to-cart path in this theme (quick-add buttons, the quickshop
     modal, the main PDP form) posts to /cart/add.js itself, then dispatches
     this one event.

     What happens next is the "After add to cart" theme setting: go to /cart
     (the default, matching the Empire control) or stay on the page, re-render
     whichever cart surface is on screen and open the drawer. Drawer mode
     keeps the toast, scroll position and browse-and-add flow; cart mode is
     what the control does, so the two themes differ only in design. */
  document.addEventListener('cart:updated', function (e) {
    /* Optimistic badge bump so the count moves on the same frame as the tap
       instead of waiting a round trip, which is what makes shoppers tap twice.
       /cart/add.js answers with the created LINE ITEM, not a cart, so there is
       no item_count to read: add its quantity to the badge and let
       refreshCartState reconcile against the real cart a moment later. A gift
       wrap line is excluded, matching shopperItemCount. */
    var detail = e && e.detail;
    if (detail && typeof detail.item_count === 'number') {
      updateCartCount(shopperItemCount(detail));
    } else if (detail && typeof detail.quantity === 'number') {
      var props = detail.properties || {};
      if (!props._gift_wrap_for) {
        var badge = document.querySelector('[data-cart-count]');
        var shown = badge ? parseInt(badge.textContent, 10) : NaN;
        if (!isNaN(shown)) updateCartCount(shown + detail.quantity);
      }
    }
    if (document.querySelector('.wtz-cart-wrapper')) {
      refreshCartState();
      refreshCartSection();
      return;
    }
    /* Theme setting "After add to cart" (default: go to cart). The control
       theme, Empire, sends every add straight to /cart (cart_redirection),
       so this matches it and an A/B test measures the design, not this. */
    if (window.Theme && window.Theme.cartAfterAdd !== 'drawer') {
      window.location.href = routes.cart_url || '/cart';
      return;
    }
    /* refreshCartDrawer reconciles the badge from the same response, and
       refreshCartUI falls back to refreshCartState when no drawer is mounted. */
    syncGiftWrap().then(refreshCartUI).then(function () {
      if (drawerEl()) openCartDrawer();
    });
  });

  /* ==================================================================
     4. LINE EDITS
     ================================================================== */
  /* Pending /cart/change.js controllers, keyed by line item key. */
  var inFlightLineChanges = {};

  /* The cart page renders every line TWICE: once in [data-cart-lines-main]
     (desktop) and again in [data-cart-lines-acc] (the mobile disclosure).
     Both copies carry the same data-cart-item key, so a quantity typed or
     stepped in one tree leaves the other showing a stale number until the
     section re-render lands. refreshCartSection is a network round trip, so
     mirror the value immediately to keep the trees from diverging in between.
     ponytail: scoped by line key rather than a global input selector, which
     would hit whichever duplicate happened to come first in the DOM. */
  function syncQtyAcrossTrees(key, value, originInput) {
    $$('[data-cart-item="' + (window.CSS && CSS.escape ? CSS.escape(key) : key) + '"]').forEach(function (row) {
      var twin = row.querySelector('[data-cart-qty-input]');
      if (!twin || twin === originInput) return;
      twin.value = value;
      twin.defaultValue = value;
    });
  }

  function changeCartItem(key, qty, wrapKey) {
    /* Not fetchJSON: this endpoint answers 422 with a JSON body explaining why,
       most often "Only N item was added to your cart due to availability", and
       fetchJSON throws on any non-ok status, which turned a precise stock
       message into a generic connection error. The body is read either way, the
       shopper is told what actually happened, and the section is re-rendered so
       the quantity on screen returns to what the cart really holds instead of
       keeping the number they typed. */
    /* Rapid +/- taps used to race: if change #1 landed after change #2, the
       cart silently reverted to the older quantity. Abort any still-pending
       request for this same line before issuing the next one.
       ponytail: keyed per line key, so two different lines never cancel each
       other. */
    var pending = inFlightLineChanges[key];
    if (pending) pending.abort();
    var ctrl = typeof AbortController === 'function' ? new AbortController() : null;
    if (ctrl) inFlightLineChanges[key] = ctrl;

    /* Gift wrap is charged per unit, so a wrapped line's wrap moves to the
       same quantity (0 included) in the SAME /cart/update.js request.
       /cart/change.js replaces one line only, and two separate calls could
       half-succeed and leave the wrap count wrong at checkout. */
    var url = (routes.cart_change_url || '/cart/change') + '.js';
    var payload = { id: key, quantity: qty };
    if (wrapKey) {
      url = (routes.cart_update_url || '/cart/update') + '.js';
      var updates = {};
      updates[key] = qty;
      updates[wrapKey] = qty;
      payload = { updates: updates };
    }

    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
      signal: ctrl ? ctrl.signal : undefined
    })
      .then(function (res) {
        return res.json().then(function (json) {
          if (!res.ok) {
            if (inFlightLineChanges[key] === ctrl) delete inFlightLineChanges[key];
            toast(json.description || json.message || 'Unable to update your cart.', { type: 'error' });
            refreshCartUI();
            throw new Error('HTTP ' + res.status);
          }
          if (inFlightLineChanges[key] === ctrl) delete inFlightLineChanges[key];
          /* Stock can cap the item below what was asked for while the wrap
             took the full number, so check the cart that came back. */
          return reconcileGiftWrap(json).then(function (fixed) {
            updateCartCount(shopperItemCount(fixed || json));
            refreshCartUI();
            return json;
          });
        });
      })
      .catch(function (err) {
        if (inFlightLineChanges[key] === ctrl) delete inFlightLineChanges[key];
        /* We aborted this ourselves because a newer tap superseded it. Not a
           failure, and the caller must not roll the input back to a stale
           value, so rethrow a marker the qty handlers ignore. */
        if (err && err.name === 'AbortError') {
          var superseded = new Error('WTZ_SUPERSEDED');
          superseded.superseded = true;
          throw superseded;
        }
        /* A rejection from the branch above has already told the shopper what
           went wrong; only a genuine network or parse failure needs the
           fallback message. */
        if (!err.superseded && !/^HTTP /.test(err.message || '')) {
          console.error('Cart update failed:', err);
          toast('Unable to update your cart. Please check your connection and try again.', { type: 'error' });
        }
        throw err;
      });
  }

  document.addEventListener('click', function (e) {
    var removeBtn = e.target.closest('[data-remove-item]');
    if (removeBtn) {
      var cartItem = removeBtn.closest('[data-cart-item]');
      var key = removeBtn.dataset.removeItem;
      removeBtn.disabled = true;
      removeBtn.textContent = 'Removing...';
      var updates = {};
      updates[key] = 0;
      if (cartItem && cartItem.dataset.giftWrapLineKey) {
        updates[cartItem.dataset.giftWrapLineKey] = 0;
      }
      fetchJSON((routes.cart_update_url || '/cart/update') + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates: updates })
      }).then(function (json) {
        return reconcileGiftWrap(json).then(function (fixed) {
          updateCartCount(shopperItemCount(fixed || json));
          refreshCartUI();
        });
      }).catch(function () {
        removeBtn.disabled = false;
        removeBtn.textContent = 'Remove';
        toast('Unable to remove item. Please try again.', { type: 'error' });
      });
      return;
    }
    var qtyBtn = e.target.closest('[data-cart-qty]');
    if (!qtyBtn) return;
    var cartItem2 = qtyBtn.closest('[data-cart-item]');
    if (!cartItem2) return;
    var input = cartItem2.querySelector('[data-cart-qty-input]');
    if (!input) return;
    var prevVal = parseInt(input.value, 10);
    var delta = parseInt(qtyBtn.dataset.cartQty, 10) || 0;
    var newVal = Math.max(0, prevVal + delta);
    input.value = newVal;
    /* The line's gift wrap (if any) moves with it in the same POST, to the
       same quantity, including 0. */
    var gwk = cartItem2.dataset.giftWrapLineKey || null;
    var lineKey = cartItem2.dataset.cartItem;
    syncQtyAcrossTrees(lineKey, newVal, input);
    changeCartItem(lineKey, newVal, gwk).catch(function (err) {
      if (err && err.superseded) return;
      input.value = prevVal;
      syncQtyAcrossTrees(lineKey, prevVal, input);
    });
  });

  /* A shopper who types a quantity directly (rather than using +/-) has no
     other way to commit it; without this, the +/- buttons would apply their
     delta on top of the un-submitted typed value instead of replacing it. */
  document.addEventListener('change', function (e) {
    const input = e.target.closest('[data-cart-qty-input]');
    if (!input) return;
    const cartItem = input.closest('[data-cart-item]');
    if (!cartItem) return;
    const newVal = Math.max(0, parseInt(input.value, 10) || 0);
    input.value = newVal;
    const prevVal = parseInt(input.defaultValue, 10) || 0;
    const gwKey = cartItem.dataset.giftWrapLineKey || null;
    const typedKey = cartItem.dataset.cartItem;
    syncQtyAcrossTrees(typedKey, newVal, input);
    changeCartItem(typedKey, newVal, gwKey).then(function () {
      input.defaultValue = newVal;
    }).catch(function (err) {
      if (err && err.superseded) return;
      input.value = prevVal;
      syncQtyAcrossTrees(typedKey, prevVal, input);
    });
  });

  /* Every gift wrap line must hold exactly as many wraps as the item it
     wraps. The handlers above keep the two in step, but some paths change an
     item without touching its wrap: adding more of a wrapped product from
     its page (Shopify merges that into the existing line), a stock limit
     capping the item, or an older cart from before wraps were per unit.
     Returns the /cart/update.js lines needed to fix `cart`, or null when it
     is already right:
       - a wrap whose item is gone goes to 0 (an orphan is hidden from the
         cart page but would still be charged at checkout)
       - a second wrap line for the same item goes to 0
       - otherwise a wrap's quantity is set to its item's quantity */
  function giftWrapFixes(cart) {
    var items = (cart && cart.items) || [];
    var itemQty = {};
    items.forEach(function (line) {
      if (!(line.properties || {})._gift_wrap_for) itemQty[line.key] = line.quantity;
    });
    var updates = {};
    var wrapped = {};
    var needed = false;
    items.forEach(function (line) {
      var forKey = (line.properties || {})._gift_wrap_for;
      if (!forKey) return;
      var want = wrapped[forKey] ? 0 : (itemQty[forKey] || 0);
      wrapped[forKey] = true;
      if (line.quantity !== want) { updates[line.key] = want; needed = true; }
    });
    return needed ? updates : null;
  }

  /* A line's key includes its discounts, so applying or removing a code
     (e.g. the member code) gives the wrapped item a new key and leaves its
     wrap pointing at one that no longer exists. Before giftWrapFixes would
     zero that wrap as an orphan, point it at the item's new key: the only
     unwrapped line of the same variant (the key starts with the variant id).
     With no such line, or more than one, the wrap stays an orphan. Returns
     the /cart/change.js bodies for those wraps, or [] when none are stale. */
  function giftWrapRelinks(cart) {
    var items = (cart && cart.items) || [];
    var itemKeys = {};
    var claimed = {};
    items.forEach(function (line) {
      var forKey = (line.properties || {})._gift_wrap_for;
      if (forKey) claimed[forKey] = true;
      else itemKeys[line.key] = true;
    });
    var relinks = [];
    items.forEach(function (line) {
      var props = line.properties || {};
      var forKey = props._gift_wrap_for;
      if (!forKey || itemKeys[forKey]) return;
      var variant = String(forKey).split(':')[0];
      var matches = items.filter(function (other) {
        return !(other.properties || {})._gift_wrap_for && !claimed[other.key] &&
          String(other.key).split(':')[0] === variant;
      });
      if (matches.length !== 1) return;
      claimed[matches[0].key] = true;
      var properties = {};
      Object.keys(props).forEach(function (name) { properties[name] = props[name]; });
      properties._gift_wrap_for = matches[0].key;
      relinks.push({ id: line.key, quantity: matches[0].quantity, properties: properties });
    });
    return relinks;
  }

  /* Applies giftWrapRelinks, then giftWrapFixes. Resolves with the corrected
     cart, or null when nothing needed changing (no request is made then) or
     the fix failed. Never rejects: callers run it after an edit that already
     succeeded, and a failed tidy must not report that edit as failed or
     block checkout. The checkout gate and the next cart render get another
     go at it. */
  function reconcileGiftWrap(cart) {
    var relinks = giftWrapRelinks(cart);
    var relinked = relinks.reduce(function (chain, body) {
      return chain.then(function () {
        return fetchJSON((routes.cart_change_url || '/cart/change') + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body)
        });
      });
    }, Promise.resolve(cart));
    return relinked.then(function (current) {
      var updates = giftWrapFixes(current);
      if (!updates) return relinks.length ? current : null;
      return fetchJSON((routes.cart_update_url || '/cart/update') + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates: updates })
      });
    }).catch(function (err) { console.error('Gift wrap reconcile failed:', err); return null; });
  }

  /* Reads the live cart and reconciles it. Resolves like reconcileGiftWrap. */
  function syncGiftWrap() {
    return fetchJSON((routes.cart_url || '/cart') + '.js')
      .then(reconcileGiftWrap)
      .catch(function (err) { console.error('Gift wrap reconcile failed:', err); return null; });
  }

  /* On /cart the lines were just rendered from the server, so check them
     once on arrival. That covers the default "go to cart after add" flow,
     where adding more of a wrapped product lands here. Re-render only if
     something changed, since the markup shows the cart before the fix. */
  if (document.querySelector('.wtz-cart-wrapper')) {
    syncGiftWrap().then(function (fixed) { if (fixed) refreshCartUI(); });
  }

  /* Museum gift wrap: adds/removes a real product, linked to the item it
     wraps via a `_gift_wrap_for` line item property. One wrap per unit, so
     checking the box on a line of 3 adds 3. Uses Section Rendering to swap
     the cart section inline instead of a full page reload, so the shopper
     keeps scroll position and any typed promo code. */
  document.addEventListener('change', function (e) {
    const toggle = e.target.closest('[data-gift-wrap-toggle]');
    if (!toggle) return;
    toggle.disabled = true;
    let request;
    if (toggle.checked) {
      request = fetchJSON((routes.cart_add_url || '/cart/add') + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: toggle.dataset.giftWrapVariant,
          quantity: parseInt(toggle.dataset.giftWrapQty, 10) || 1,
          /* "Gift wrap for" shows at checkout and on the order so the packer
             knows which item to wrap; `_gift_wrap_for` is the hidden link. */
          properties: {
            _gift_wrap_for: toggle.dataset.giftWrapFor,
            'Gift wrap for': toggle.dataset.giftWrapLabel || ''
          }
        })
      });
    } else {
      request = fetchJSON((routes.cart_change_url || '/cart/change') + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: toggle.dataset.giftWrapKey, quantity: 0 })
      });
    }
    /* /cart/add.js answers with the added LINE, which has no item_count;
       reading it as a cart would flash the badge to 0. The refresh below
       reconciles the badge either way. */
    request.then(function (cart) {
      if (typeof cart.item_count === 'number') updateCartCount(shopperItemCount(cart));
      return refreshCartUI();
    }).catch(function () {
      toggle.disabled = false;
      toggle.checked = !toggle.checked;
      toast('Unable to update gift wrap. Please try again.', { type: 'error' });
    });
  });

  /* ==================================================================
     5. DISCOUNT CODE
     ================================================================== */
  /* Shopify's cart update endpoint has no discount-code field; the correct
     way to apply one is its native /discount/{code} route, which sets the
     discount on the checkout session and redirects back. */
  function applyDiscountCode(input) {
    const code = input && input.value.trim();
    if (!code) return;
    window.location.href = '/discount/' + encodeURIComponent(code) + '?redirect=' + encodeURIComponent(routes.cart_url || '/cart');
  }
  document.addEventListener('click', function (e) {
    const applyBtn = e.target.closest('[data-apply-discount-code]');
    if (!applyBtn) return;
    applyDiscountCode(applyBtn.parentElement.querySelector('[data-discount-code-input]'));
  });
  document.addEventListener('keydown', function (e) {
    const input = e.target.closest('[data-discount-code-input]');
    if (!input || e.key !== 'Enter') return;
    e.preventDefault();
    applyDiscountCode(input);
  });

  /* ==================================================================
     6. CHECKOUT GATE
     ================================================================== */
  /* Before checkout, reconcile gift wrap (giftWrapFixes): drop any wrap
     whose item has left the cart, and match every other wrap's quantity to
     its item. An orphan is hidden from the cart page but Shopify would still
     charge for it at checkout. */
  document.addEventListener('submit', function (e) {
    var form = e.target.closest('form[action$="/cart"]');
    if (!form) return;
    var checkoutBtn = form.querySelector('[name="checkout"]');
    if (!checkoutBtn) return;
    /* iOS WebKit and several in-app browsers leave e.submitter undefined, so a
       submitter-only guard silently dropped every mobile checkout through this
       handler. Fall back to the focused element, then to "this form has a
       checkout button and nothing else claimed the submit". */
    var isCheckout = (e.submitter && e.submitter === checkoutBtn) ||
      (document.activeElement && document.activeElement === checkoutBtn) ||
      !e.submitter;
    if (!isCheckout) return;
    e.preventDefault();
    checkoutBtn.disabled = true;
    checkoutBtn.textContent = 'Redirecting to checkout...';

    /* FAIL-OPEN. This handler exists only to tidy gift-wrap lines;
       it must never be what stops a shopper reaching checkout. Two chained
       round trips on slow cellular could leave the button stuck disabled
       forever, since a stalled request rejects nothing and hits no catch.
       ponytail: 1.5s ceiling. An orphan that outlives the timeout is charged
       at checkout, which the server-side cleanup in the remove/qty handlers
       now prevents at the source. */
    var navigated = false;
    var go = function () {
      if (navigated) return;
      navigated = true;
      window.location.href = '/checkout';
    };
    var forceCheckout = setTimeout(go, 1500);
    var done = function () { clearTimeout(forceCheckout); go(); };

    /* preventDefault() above means the form never posts, so the gift message
       textarea (name="note", attached via form="...") has to be saved here or
       it never reaches the order. The debounced input save below usually has
       it stored already; this catches the last keystrokes. */
    var noteField = form.elements.namedItem('note');
    var saveNote = noteField ? postNote(noteField.value) : Promise.resolve();

    var cleanup = syncGiftWrap();

    Promise.all([saveNote.catch(function () {}), cleanup.catch(function () {})]).then(done);
  });

  /* Gift message: save as the shopper types, so the note is already on the
     cart before checkout (and survives a reload or a drawer checkout). */
  function postNote(note) {
    return fetchJSON((routes.cart_update_url || '/cart/update') + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ note: note })
    });
  }
  var noteTimer;
  document.addEventListener('input', function (e) {
    if (e.target.name !== 'note' || !e.target.closest('.wtz-gift-box')) return;
    clearTimeout(noteTimer);
    var value = e.target.value;
    noteTimer = setTimeout(function () { postNote(value).catch(function () {}); }, 600);
  });

  /* ==================================================================
     7. EXPERIMENT TAG
     ================================================================== */
  /* Records which theme rendered the session that produced an order, so the
     rollout experiment can be measured on orders rather than on sessions.

     A cart attribute is the only storefront signal that survives into the
     order: checkout runs on Shopify's own pages where this theme does not
     execute, so a dataLayer variable or a JS global set here is gone before
     the purchase event. The attribute rides along with the cart, lands on
     the order record, and is readable in Admin and in an orders CSV export
     without depending on GTM, GA4, consent state or ad blockers.

     Set from JS so it lands on every session regardless of which page the
     shopper checks out from.

     The leading underscore makes it private: it stays off the order
     confirmation and customer-facing display, since this is our bookkeeping
     rather than the shopper's business.

     Written once per page load and only when the value would change, so it
     costs one POST per session rather than one per cart mutation. Failure is
     silent by design: losing an experiment label must never interrupt a
     shopper's checkout.

     ponytail: remove this whole block once the rollout finishes and the
     winning theme is published to 100%. */
  function tagThemeVariant() {
    const theme = window.Shopify && window.Shopify.theme;
    const name = theme && theme.name;
    if (!name) return;

    fetchJSON((routes.cart_url || '/cart') + '.js')
      .then(function (cart) {
        if (cart && cart.attributes && cart.attributes._theme_variant === name) return;
        return fetchJSON('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: { _theme_variant: name } })
        });
      })
      .catch(function () { /* analytics only; never block checkout */ });
  }

  /* ponytail: defer the variant tag so it never races a checkout click.
     requestIdleCallback lets the browser settle first; skip entirely on
     /cart and /checkout where a stale-cart conflict costs real money. */
  if (!/\/(cart|checkout)(\/|$|\?)/.test(window.location.pathname)) {
    (window.requestIdleCallback || function (cb) { setTimeout(cb, 2000); })(tagThemeVariant);
  }
})();
